import { useMemo } from 'react'

import {
  useCubeReportAccessFiltersContext,
  useCubeReportPageFiltersContext,
} from 'cube'
import {
  CONVERSATION_FACTS_DIMENSIONS,
  CONVERSATION_FACTS_MEASURES,
} from 'cube/constants/QueryConstants'
import { isEmpty, isNil } from 'ramda'

import {
  type ChartSeriesData,
  CubeQueryBuilder,
  useCubeQuery,
} from '@driftt/dds.reports'

import useTimePeriod from 'hooks/useTimePeriod'

export interface ConversationCountsByDayQueryResult {
  metric: ChartSeriesData
  previousMetric: ChartSeriesData
  isLoading: boolean
  hasNoData: boolean
  hasError: boolean
  metricTotal: number
}

const resolveCubeResults = (data: any, queryName: string) => {
  if (isNil(data) || isEmpty(data.rawData())) {
    return []
  }

  return data
    .chartPivot()
    .map((item: object) => [item['x'], parseFloat(item[queryName])])
}

/***
 * Hook that uses cube to fetch data for the # of conversations aggregated per-day
 *
 * @returns {ConversationCountsByDayQueryResult}
 */
export const useConversationCountsByDay =
  (): ConversationCountsByDayQueryResult => {
    const timePeriod = useTimePeriod()
    const { pageFilters } = useCubeReportPageFiltersContext()
    const { accessFilters } = useCubeReportAccessFiltersContext()
    const cubeQueryCurrent = new CubeQueryBuilder({})
      .withName('ConversationQueryCountsByDay')
      .withFilters([...pageFilters, ...accessFilters])
      .withMeasures([CONVERSATION_FACTS_MEASURES.CONVERSATION_COUNT])
      .withinDateRange(
        CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
        timePeriod.startDate.toDate(),
        timePeriod.endDate.toDate(),
        'day'
      )
      .build()

    const cubeQueryPrevious = new CubeQueryBuilder({})
      .withName('ConversationQueryCountsByDayPrevious')
      .withFilters([...pageFilters, ...accessFilters])
      .withMeasures([CONVERSATION_FACTS_MEASURES.CONVERSATION_COUNT])
      .withinDateRange(
        CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
        timePeriod.prevStart.toDate(),
        timePeriod.prevEnd.toDate(),
        'day'
      )
      .build()

    const {
      data: resultSetCurrent,
      isLoading: isLoadingCurrent,
      isError: isErrorCurrent,
    } = useCubeQuery({
      query: cubeQueryCurrent,
    })

    const { data: resultSetPrevious, isLoading: isLoadingPrevious } =
      useCubeQuery({
        query: cubeQueryPrevious,
      })

    const conversationCountCurrent = useMemo(
      () => resultSetCurrent,
      [resultSetCurrent]
    )

    const conversationCountPrevious = useMemo(
      () => resultSetPrevious,
      [resultSetPrevious]
    )

    const metric = useMemo(
      () =>
        resolveCubeResults(
          conversationCountCurrent,
          CONVERSATION_FACTS_MEASURES.CONVERSATION_COUNT
        ),
      [conversationCountCurrent]
    )

    const metricTotal = useMemo(
      () =>
        metric.map((seriesPoint) => seriesPoint[1]).reduce((a, b) => a + b, 0),
      [metric]
    )

    const previousMetric = useMemo(
      () =>
        resolveCubeResults(
          conversationCountPrevious,
          CONVERSATION_FACTS_MEASURES.CONVERSATION_COUNT
        ),
      [conversationCountPrevious]
    )

    const isLoading = useMemo(
      () => isLoadingCurrent || isLoadingPrevious,
      [isLoadingCurrent, isLoadingPrevious]
    )

    const hasNoData = useMemo(
      () => !isLoading && isNil(conversationCountCurrent),
      [isLoading, conversationCountCurrent]
    )

    const hasError = useMemo(
      () => !isLoading && isErrorCurrent,
      [isLoading, isErrorCurrent]
    )

    return useMemo(
      () => ({
        metric,
        previousMetric,
        isLoading,
        hasNoData,
        hasError,
        metricTotal,
      }),
      [metric, previousMetric, isLoading, hasNoData, hasError, metricTotal]
    )
  }

export default useConversationCountsByDay
